import React, { useEffect, useState, useCallback } from "react";
import {
  Radar, TrendingDown, AlertTriangle, Award, PackageX, Trash2, Settings2, Boxes,
  FileText, FileSpreadsheet, CalendarRange, Flame,
} from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Select } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum, fmtUnit } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";

const SEV_TONE = { WARNING: "warn", CRITICAL: "danger", SEVERE: "danger" };
const SEV_LABEL = { WARNING: "Warning", CRITICAL: "Critical", SEVERE: "Severe" };

function Stat({ label, value, tone = "ink", testid }) {
  const tones = { ink: "text-ink", danger: "text-danger-text", success: "text-success-text", warn: "text-warn-text" };
  return (
    <div className="rounded-2xl border border-line bg-white p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink-mute">{label}</p>
      <p className={`mt-1 font-heading text-2xl font-bold ${tones[tone]}`} data-testid={testid}>{value}</p>
    </div>
  );
}

export default function Leakage() {
  const toast = useToast();
  const { isRole } = useAuth();
  const { t } = useT();
  const isHQ = isRole("SUPER_ADMIN", "OWNER", "CENTRAL_PURCHASING", "WAREHOUSE");
  const canEditSettings = isRole("SUPER_ADMIN", "OWNER");

  const [overview, setOverview] = useState(null);
  const [selBranch, setSelBranch] = useState("");
  const [periods, setPeriods] = useState([]);
  const [selPeriod, setSelPeriod] = useState("");
  const [variance, setVariance] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [exporting, setExporting] = useState("");
  const [threshold, setThreshold] = useState("");
  const [savingT, setSavingT] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isHQ) {
      api.get("/leakage").then(({ data }) => {
        setOverview(data);
        const firstWithData = data.ranking.find((r) => r.has_data) || data.ranking[0];
        if (firstWithData) setSelBranch(firstWithData.branch_id);
        setLoaded(true);
      }).catch(() => setLoaded(true));
      api.get("/settings").then(({ data }) => setThreshold(String(data.leakage_value_threshold ?? 50000)));
      api.get("/leakage/weekly-summary").then(({ data }) => setWeekly(data)).catch(() => {});
    } else {
      setLoaded(true); // branch staff: own branch is implicit
    }
  }, [isHQ]);

  const loadPeriods = useCallback(() => {
    const qs = isHQ && selBranch ? `?branch_id=${selBranch}` : "";
    if (isHQ && !selBranch) return;
    api.get(`/variance/periods${qs}`).then(({ data }) => {
      setPeriods(data.periods);
      setSelPeriod(data.periods[0] ? data.periods[0].id : "");
    }).catch(() => setPeriods([]));
  }, [isHQ, selBranch]);
  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  useEffect(() => {
    if (isHQ && !selBranch) return;
    const p = periods.find((x) => x.id === selPeriod);
    const params = new URLSearchParams();
    if (isHQ && selBranch) params.set("branch_id", selBranch);
    if (p) { params.set("start_id", p.start_id); params.set("end_id", p.end_id); }
    api.get(`/variance?${params.toString()}`).then(({ data }) => setVariance(data)).catch(() => setVariance({ no_data: true }));
  }, [selBranch, selPeriod, periods, isHQ]);

  const saveThreshold = async () => {
    setSavingT(true);
    try {
      await api.put("/settings", { leakage_value_threshold: Number(threshold) });
      toast.success(t("Leakage threshold updated"));
      const { data } = await api.get("/leakage"); setOverview(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setSavingT(false); }
  };

  const exportVariance = async (fmt) => {
    setExporting(fmt);
    try {
      const p = periods.find((x) => x.id === selPeriod);
      const params = new URLSearchParams();
      if (isHQ && selBranch) params.set("branch_id", selBranch);
      if (p) { params.set("start_id", p.start_id); params.set("end_id", p.end_id); }
      const ext = fmt === "pdf" ? "pdf" : "xlsx";
      const { data, headers } = await api.get(`/variance/export.${ext}?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));
      const a = document.createElement("a");
      a.href = url; a.download = `variance-report.${ext}`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t("Export failed — needs two finalized stock takes"));
    }
    setExporting("");
  };

  if (!loaded) return <PageLoader />;

  const sum = variance && !variance.no_data ? variance.summary : null;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={t("Leakage & Variance")} subtitle={t("Expected vs actual usage between stock takes — unexplained loss")} icon={Radar}>
        {canEditSettings && (
          <div className="flex items-center gap-1.5">
            <Settings2 size={15} className="text-ink-mute" />
            <span className="hidden text-xs text-ink-mute sm:block">{t("Flag > Rp")}</span>
            <input type="number" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-24 rounded-lg border border-line px-2 py-1 text-sm" data-testid="leakage-threshold-input" />
            <Button size="sm" variant="outline" onClick={saveThreshold} loading={savingT} data-testid="save-threshold">{t("Save")}</Button>
          </div>
        )}
      </PageHeader>

      {/* HQ: company totals + ranking */}
      {isHQ && overview && (
        <>
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3" data-testid="leakage-totals">
            <Stat label={t("Company Leakage")} value={fmtRp(overview.totals.company_leakage_value)} tone="danger" testid="company-leakage" />
            <Stat label={t("Suspicious Items")} value={overview.totals.total_suspicious} tone="warn" testid="total-suspicious" />
            <Stat label={t("Branches Analyzed")} value={overview.totals.branches_with_data} testid="branches-analyzed" />
          </div>

          <Card className="mb-6 overflow-hidden p-0" data-testid="branch-ranking">
            <div className="flex items-center gap-2 border-b border-line px-5 py-4">
              <Award size={18} className="text-primary" /><h3 className="font-heading text-lg font-bold">{t("Branch Leakage Ranking")}</h3>
              <span className="ml-auto text-xs text-ink-mute">{t("Best = least leakage")}</span>
            </div>
            <div className="divide-y divide-line">
              {overview.ranking.map((r) => (
                <button key={r.branch_id} onClick={() => r.has_data && setSelBranch(r.branch_id)} disabled={!r.has_data}
                  className={`flex w-full items-center gap-3 px-5 py-3 text-left transition-colors ${selBranch === r.branch_id ? "bg-primary-light/40" : "hover:bg-stone-50"} ${!r.has_data ? "opacity-60" : ""}`} data-testid={`rank-${r.branch_name}`}>
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-100 text-sm font-bold text-ink-soft">{r.rank ?? "–"}</span>
                  <span className="min-w-0 flex-1 truncate font-semibold text-ink">{r.branch_name}</span>
                  {r.has_data ? (
                    <>
                      <span className="hidden text-xs text-ink-mute sm:block">{r.suspicious_count} {t("flagged")}</span>
                      <Badge tone={r.leakage_pct == null ? "mute" : Math.abs(r.leakage_pct) > 10 ? "danger" : Math.abs(r.leakage_pct) > 5 ? "warn" : "success"}>{r.leakage_pct != null ? `${r.leakage_pct}%` : "—"}</Badge>
                      <span className="w-28 text-right text-sm font-bold text-danger-text">{fmtRp(r.leakage_value)}</span>
                    </>
                  ) : <Badge tone="mute">{t("No stock-take pair")}</Badge>}
                </button>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Weekly Leakage Summary (HQ digest) */}
      {isHQ && weekly && (
        <Card className="mb-6 p-5" data-testid="weekly-leakage-summary">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <CalendarRange size={18} className="text-primary" />
            <h3 className="font-heading text-lg font-bold">{t("Weekly Leakage Summary")}</h3>
            <Badge tone="info">{(weekly.week_start || "").slice(5)} – {(weekly.week_end || "").slice(5)}</Badge>
            {weekly.new_periods_this_week > 0 && <Badge tone="warn">{weekly.new_periods_this_week} {t("new this week")}</Badge>}
          </div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Stat label={t("Company Leakage")} value={fmtRp(weekly.company.company_leakage_value)} tone="danger" testid="weekly-company-leakage" />
            <Stat label={t("Suspicious Items")} value={weekly.company.total_suspicious} tone="warn" testid="weekly-suspicious" />
            <Stat label={t("Branches Analyzed")} value={weekly.company.branches_with_data} testid="weekly-branches" />
            <div className="rounded-2xl border border-line bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-ink-mute">{t("Highest Leakage Branch")}</p>
              <p className="mt-1 flex items-center gap-1.5 font-heading text-lg font-bold text-danger-text" data-testid="weekly-worst-branch">
                {weekly.worst_branch ? <><Flame size={16} /> {weekly.worst_branch.branch_name}</> : "—"}
              </p>
            </div>
          </div>
          {weekly.top_products.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-mute">{t("Top Leakage Products")}</p>
              <div className="flex flex-wrap gap-2" data-testid="weekly-top-products">
                {weekly.top_products.slice(0, 6).map((p) => (
                  <span key={p.product_id} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-stone-50 px-2.5 py-1 text-xs">
                    <span className="font-semibold text-ink">{p.product_name}</span>
                    <span className={p.leakage_value >= 0 ? "text-danger-text" : "text-success-text"}>{fmtRp(Math.abs(p.leakage_value))}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Branch / period selector */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {isHQ && (
          <Select value={selBranch} onChange={(e) => setSelBranch(e.target.value)} className="w-52" data-testid="variance-branch-select">
            {(overview?.ranking || []).map((r) => <option key={r.branch_id} value={r.branch_id} disabled={!r.has_data}>{r.branch_name}{!r.has_data ? ` (${t("no data")})` : ""}</option>)}
          </Select>
        )}
        {periods.length > 0 && (
          <Select value={selPeriod} onChange={(e) => setSelPeriod(e.target.value)} className="w-64" data-testid="variance-period-select">
            {periods.map((p) => <option key={p.id} value={p.id}>{p.label} ({(p.end_date || "").slice(0, 10)})</option>)}
          </Select>
        )}
        {variance && !variance.no_data && (
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportVariance("pdf")} loading={exporting === "pdf"} data-testid="export-variance-pdf"><FileText size={15} /> {t("PDF")}</Button>
            <Button size="sm" variant="outline" onClick={() => exportVariance("excel")} loading={exporting === "excel"} data-testid="export-variance-excel"><FileSpreadsheet size={15} /> {t("Excel")}</Button>
          </div>
        )}
      </div>

      {/* Variance detail */}
      {!variance || variance.no_data ? (
        <Card className="p-10 text-center text-ink-mute" data-testid="variance-no-data">
          <Boxes className="mx-auto mb-3 text-ink-mute" size={28} />
          {variance?.message || t("Variance analysis needs at least two finalized stock takes for this branch.")}
        </Card>
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="variance-summary">
            <Stat label={t("Expected Usage Value")} value={fmtRp(sum.total_expected_value)} testid="exp-value" />
            <Stat label={t("Actual Usage Value")} value={fmtRp(sum.total_actual_value)} testid="act-value" />
            <Stat label={t("Leakage Value")} value={fmtRp(sum.total_leakage_value)} tone="danger" testid="leak-value" />
            <Stat label={t("Leakage %")} value={sum.leakage_pct != null ? `${sum.leakage_pct}%` : "—"} tone="warn" testid="leak-pct" />
          </div>

          {variance.suspicious.length > 0 && (
            <Card className="mb-5 p-5" data-testid="suspicious-items">
              <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-bold"><AlertTriangle size={18} className="text-danger" /> {t("Suspicious Items")} ({variance.suspicious.length})</h3>
              <div className="flex flex-wrap gap-2">
                {variance.suspicious.map((r) => (
                  <span key={r.product_id} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${r.severity === "SEVERE" ? "border-danger bg-danger-light text-danger-text" : r.severity === "CRITICAL" ? "border-danger/50 bg-danger-light/60 text-danger-text" : "border-warn bg-warn-light text-warn-text"}`} data-testid={`suspicious-${r.product_name}`}>
                    {r.product_name} · {r.variance_pct != null ? `${r.variance_pct}%` : t("off-recipe")} · {fmtRp(r.leakage_value)} <Badge tone={SEV_TONE[r.severity]}>{t(SEV_LABEL[r.severity])}</Badge>
                  </span>
                ))}
              </div>
            </Card>
          )}

          <Card className="overflow-hidden p-0" data-testid="variance-table">
            <div className="flex items-center gap-2 border-b border-line px-5 py-4">
              <TrendingDown size={18} className="text-primary" /><h3 className="font-heading text-base font-bold">{t("Ingredient Variance")}</h3>
              <span className="ml-auto text-xs text-ink-mute">{variance.period.label}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                  <th className="px-4 py-3">{t("Ingredient")}</th><th className="px-4 py-3 text-right">{t("Opening")}</th><th className="px-4 py-3 text-right">{t("Received")}</th>
                  <th className="px-4 py-3 text-right">{t("Closing")}</th><th className="px-4 py-3 text-right">{t("Waste")}</th><th className="px-4 py-3 text-right">{t("Actual")}</th>
                  <th className="px-4 py-3 text-right">{t("Expected")}</th><th className="px-4 py-3 text-right">{t("Variance")}</th><th className="px-4 py-3 text-right">{t("Leakage")}</th><th className="px-4 py-3 text-center">{t("Flag")}</th></tr></thead>
                <tbody>
                  {variance.rows.map((r) => (
                    <tr key={r.product_id} className={`border-b border-line/60 ${r.severity ? "bg-danger-light/20" : ""}`} data-testid={`variance-row-${r.product_name}`}>
                      <td className="px-4 py-2.5 font-semibold">{r.product_name} <span className="text-[11px] font-normal text-ink-mute">{fmtUnit(r.unit)}</span></td>
                      <td className="px-4 py-2.5 text-right">{fmtNum(r.opening)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtNum(r.received)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtNum(r.closing)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtNum(r.waste)}</td>
                      <td className="px-4 py-2.5 text-right font-medium">{fmtNum(r.actual_usage)}</td>
                      <td className="px-4 py-2.5 text-right">{fmtNum(r.expected_usage)}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${r.variance > 0 ? "text-danger-text" : r.variance < 0 ? "text-blue-700" : "text-ink-mute"}`}>{r.variance > 0 ? "+" : ""}{fmtNum(r.variance)}{r.variance_pct != null && <span className="text-[11px]"> ({r.variance_pct}%)</span>}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-danger-text">{fmtRp(r.leakage_value)}</td>
                      <td className="px-4 py-2.5 text-center">{r.severity ? <Badge tone={SEV_TONE[r.severity]}>{t(SEV_LABEL[r.severity])}</Badge> : <span className="text-ink-mute">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Card className="p-5" data-testid="top-loss">
              <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-bold"><PackageX size={18} className="text-danger" /> {t("Top Missing / Loss")}</h3>
              {variance.top_loss.length === 0 ? <p className="text-sm text-ink-mute">{t("No positive variance items.")}</p> : variance.top_loss.map((r) => (
                <div key={r.product_id} className="flex items-center justify-between border-b border-line/60 py-2 last:border-0">
                  <span className="text-sm font-medium">{r.product_name}</span>
                  <span className="text-sm font-bold text-danger-text">+{fmtNum(r.variance)} {fmtUnit(r.unit)} · {fmtRp(r.leakage_value)}</span>
                </div>
              ))}
            </Card>
            <Card className="p-5" data-testid="top-waste">
              <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-bold"><Trash2 size={18} className="text-warn-text" /> {t("Top Waste")}</h3>
              {variance.top_waste.length === 0 ? <p className="text-sm text-ink-mute">{t("No waste recorded in this period.")}</p> : variance.top_waste.map((r) => (
                <div key={r.product_id} className="flex items-center justify-between border-b border-line/60 py-2 last:border-0">
                  <span className="text-sm font-medium">{r.product_name}</span>
                  <span className="text-sm font-bold text-warn-text">{fmtNum(r.waste)} {fmtUnit(r.unit)} · {fmtRp(r.waste * r.unit_cost)}</span>
                </div>
              ))}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
